import { Component, DestroyRef, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Dialog } from 'primeng/dialog';
import { SharedModule } from 'primeng/api';
import { UserAutocompleteComponent } from 'src/app/templates/user-autocomplete/user-autocomplete.component';
import { MfgappsApiService } from '../../../services/mfgapps-api.service';
import { MfgappsEventService } from '../../../services/mfgapps-event.service';
import { SupportHubUserLookup, SupportHubRoleLookup } from '../support-hub.models';
import { SupportHubStore } from '../support-hub.store';

interface UpdateUserPayload {
  role_id: number;
  usecase_id?: number;
  team_id?: number;
}

interface CreateUserPayload {
  email_id: string;
  user_id: number;
  user_name: string;
  role_id: number;
  usecase_id?: number;
}

interface UserModel extends SupportHubUserLookup {
  selectedRoleId?: number;
  isAdmin?: boolean;
  isDeveloper?: boolean;
  avatarLabel?: string;
}

@Component({
  selector: 'app-support-hub-role-assignment',
  standalone: true,
  imports: [FormsModule, MatProgressSpinnerModule, Dialog, SharedModule, UserAutocompleteComponent],
  templateUrl: './support-hub-role-assignment.component.html',
  styleUrl: './support-hub-role-assignment.component.scss'
})
export class SupportHubRoleAssignmentComponent {
  private readonly destroyRef = inject(DestroyRef);
  selectedUseCaseId: number | null = null;
  searchUserQuery = '';
  users: UserModel[] = [];
  filteredUsers: UserModel[] = [];
  roleOptions: SupportHubRoleLookup[] = [];
  loader = { isLoading: false, message: '' };
  currentRole: string = SupportHubStore.ROLE_USER;
  isAddUserDialogVisible = false;
  dialogLoader = { isLoading: false, message: '' };
  isAddUserIdentityLocked = false;
  dialogFormSubmitted = false;
  newUserModal = {
    email_id: '',
    user_id: '',
    user_name: '',
    role_id: null as number | null
  };

  padIndex(index: number): string {
    return String(index + 1).padStart(2, '0');
  }

  constructor(
    public readonly store: SupportHubStore,
    private readonly apiService: MfgappsApiService,
    private readonly eventService: MfgappsEventService
  ) {}

  async ngOnInit(): Promise<void> {
    this.currentRole = this.store.currentUserRoleValue;
    this.store.currentUserRoleSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(role => {
      this.currentRole = role;
    });
    await this.store.loadLookup();
    this.roleOptions = this.store.lookupValue.roles.filter(role => role.role_name !== 'User');
    this.store.selectedUseCaseIdSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async useCaseId => {
      const nextUseCaseId = useCaseId ?? this.store.getDefaultUseCaseId();
      if (nextUseCaseId === this.selectedUseCaseId) {
        return;
      }
      this.selectedUseCaseId = nextUseCaseId;
      await this.loadUsers();
    });
  }

  get isAdminRole(): boolean {
    return this.store.isAdminRoleName(this.currentRole);
  }

  getRoleLabel(roleId: number | null | undefined): string {
    if (!roleId) {
      return 'No Role';
    }
    return this.roleOptions.find(r => r.role_id === roleId)?.role_name || 'No Role';
  }

  getAssignableRoleNames(): string {
    return this.roleOptions.map(r => r.role_name).join(', ');
  }

  onSearchChange(): void {
    if (!this.searchUserQuery.trim()) {
      this.filteredUsers = [...this.users];
      return;
    }

    const query = this.searchUserQuery.toLowerCase();
    this.filteredUsers = this.users.filter(user =>
      user.user_name.toLowerCase().includes(query) ||
      user.email_id.toLowerCase().includes(query)
    );
  }

  openAddUserModal(): void {
    this.newUserModal = {
      email_id: '',
      user_id: '',
      user_name: '',
      role_id: null
    };
    this.isAddUserIdentityLocked = false;
    this.dialogFormSubmitted = false;
    this.isAddUserDialogVisible = true;
  }

  onAddUserSelected(event: any): void {
    const selectedUser = event?.selectedUser;
    if (!selectedUser) {
      return;
    }

    const email = String(selectedUser.userPrincipalName ?? selectedUser.mail ?? '').trim();
    const userName = String(selectedUser.displayName ?? '').trim();
    const userId = this.extractNumericUserId(selectedUser, email);

    this.newUserModal.email_id = email;
    this.newUserModal.user_name = userName;
    this.newUserModal.user_id = userId ? String(userId) : '';
    this.isAddUserIdentityLocked = true;

    if (!userId) {
      this.eventService.showSnackBar('Unable to derive numeric user ID from selected user. Please choose another user.', 'warn');
    }
  }

  closeAddUserDialog(): void {
    if (!this.dialogLoader.isLoading) {
      this.isAddUserDialogVisible = false;
      this.dialogFormSubmitted = false;
    }
  }

  async addUser(): Promise<void> {
    this.dialogFormSubmitted = true;
    if (!this.isAddUserIdentityLocked || !this.newUserModal.role_id) {
      this.eventService.showSnackBar('Please search and select a user, then select a role', 'error');
      return;
    }
    if (!Number(this.newUserModal.user_id)) {
      this.eventService.showSnackBar('User ID must be a valid number', 'error');
      return;
    }
    this.setDialogLoader('Adding user...');
    try {
      const payload: CreateUserPayload = {
        email_id: this.newUserModal.email_id,
        user_id: Number(this.newUserModal.user_id),
        user_name: this.newUserModal.user_name,
        role_id: this.newUserModal.role_id,
        usecase_id: this.selectedUseCaseId ?? undefined
      };
      const response = await this.apiService.createFeedbackUser(payload);
      if (response?.message && response.message !== 'Success') {
        this.eventService.showSnackBar(response?.error ?? 'Unable to add user', 'error');
        return;
      }
      this.eventService.showSnackBar('User added successfully', 'success');
      this.isAddUserDialogVisible = false;
      this.setLoader('Loading users...');
      try {
        await this.loadUsers();
      }
      finally {
        this.resetLoader();
      }
    }
    catch (error: any) {
      this.eventService.showSnackBar(error?.message ?? 'Unable to add user', 'error');
    }
    finally {
      this.resetDialogLoader();
    }
  }

  async updateUserRole(user: UserModel): Promise<void> {
    if (!user.id || user.selectedRoleId === undefined) {
      return;
    }
    this.setLoader('Updating user...');
    try {
      const payload: UpdateUserPayload = {
        role_id: user.selectedRoleId,
        usecase_id: user.usecase_id ?? undefined,
        team_id: user.team_id ?? undefined
      };
      const response = await this.apiService.updateFeedbackUser(user.id, payload);
      if (response?.message && response.message !== 'Success') {
        this.eventService.showSnackBar(response?.error ?? 'Unable to update user', 'error');
        return;
      }
      user.role_id = user.selectedRoleId;
      user.isAdmin = this.getRoleLabel(user.role_id) === SupportHubStore.ROLE_ADMIN_SUFFIX;
      user.isDeveloper = this.getRoleLabel(user.role_id) === SupportHubStore.ROLE_DEVELOPER;
      this.eventService.showSnackBar('User role updated successfully', 'success');
    }
    catch (error: any) {
      this.eventService.showSnackBar(error?.message ?? 'Unable to update user', 'error');
    }
    finally {
      this.resetLoader();
    }
  }

  private async loadUsers(): Promise<void> {
    if (!this.selectedUseCaseId) {
      this.users = [];
      this.filteredUsers = [];
      return;
    }
    this.setLoader('Loading users...');
    try {
      const response = await this.apiService.getFeedbackUsers(this.selectedUseCaseId);
      const userList = Array.isArray(response?.result) ? response.result : [];
      this.users = userList.map(user => ({
        ...user,
        selectedRoleId: user.role_id,
        isAdmin: this.getRoleLabel(user.role_id) === SupportHubStore.ROLE_ADMIN_SUFFIX,
        isDeveloper: this.getRoleLabel(user.role_id) === SupportHubStore.ROLE_DEVELOPER,
        avatarLabel: this.getAvatarLabel(user.user_name)
      }));
      this.filteredUsers = [...this.users];
    }
    finally {
      this.resetLoader();
    }
  }

  private getAvatarLabel(userName: string | null | undefined): string {
    const rawName = String(userName ?? '').trim();
    if (!rawName) {
      return '--';
    }

    const nameBeforeParenthesis = rawName.split('(')[0]?.trim() ?? '';
    if (!nameBeforeParenthesis) {
      return '--';
    }

    const parts = nameBeforeParenthesis.split(/\s+/).filter(Boolean);
    const firstInitial = parts[0]?.charAt(0) ?? '';
    const lastInitial = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';

    return `${firstInitial}${lastInitial}`.toUpperCase() || '--';
  }

  private setLoader(message: string): void {
    this.loader.isLoading = true;
    this.loader.message = message;
  }

  private resetLoader(): void {
    this.loader.isLoading = false;
    this.loader.message = '';
  }

  private setDialogLoader(message: string): void {
    this.dialogLoader.isLoading = true;
    this.dialogLoader.message = message;
  }

  private resetDialogLoader(): void {
    this.dialogLoader.isLoading = false;
    this.dialogLoader.message = '';
  }

  isFieldInvalid(fieldValue: any): boolean {
    return this.dialogFormSubmitted && !fieldValue;
  }

  private extractNumericUserId(selectedUser: any, email: string): number | null {
    const employeeId = String(selectedUser.employeeId ?? '').trim();
    if (/^\d+$/.test(employeeId)) {
      return Number(employeeId);
    }

    const mailNickname = String(selectedUser.mailNickname ?? '').trim();
    const fromMailNickname = /\d{6,}/.exec(mailNickname);
    if (fromMailNickname?.[0]) {
      return Number(fromMailNickname[0]);
    }

    const fromDisplayName = /\b\d{6,}\b/.exec(String(selectedUser.displayName ?? ''));
    if (fromDisplayName?.[0]) {
      return Number(fromDisplayName[0]);
    }

    const localPart = email.split('@')[0] ?? '';
    const fromUpn = /\d{6,}/.exec(localPart);
    if (fromUpn?.[0]) {
      return Number(fromUpn[0]);
    }

    return null;
  }
}